import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, RotateCcw, Dumbbell, Target, Shield, Repeat, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function Timer() {
  const [sec, setSec] = useState(0);
  const [run, setRun] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (run) ref.current = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(ref.current);
  }, [run]);
  const mm = String(Math.floor(sec / 60)).padStart(2, "0");
  const ss = String(sec % 60).padStart(2, "0");
  return (
    <div className="border border-border rounded-xl bg-card p-6 text-center" data-testid="workout-timer">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Session Timer</p>
      <div className="font-display font-900 text-6xl tracking-tight my-2">{mm}:{ss}</div>
      <div className="flex justify-center gap-3">
        <button onClick={() => setRun((r) => !r)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase font-bold">{run ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Start</>}</button>
        <button onClick={() => { setSec(0); setRun(false); }} className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full text-xs uppercase font-bold hover:border-primary transition-colors"><RotateCcw size={14} /> Reset</button>
      </div>
    </div>
  );
}

export default function WorkoutDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  useEffect(() => { api.get(`/content/workouts/${id}`).then((r) => setW(r.data)).catch(() => {}); }, [id]);
  if (!w) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const complete = async () => {
    try { await api.post("/me/log", { workout_done: true, calories: w.calories }); toast.success("Workout complete! +10 pts"); }
    catch { toast.error("Could not log."); }
  };

  const stats = [
    { icon: Layers, l: "Sets", v: w.sets }, { icon: Repeat, l: "Reps", v: w.reps },
    { icon: Dumbbell, l: "Equipment", v: w.equipment }, { icon: Target, l: "Target", v: w.target },
  ];

  return (
    <div data-testid="workout-detail-page" className="px-4 md:px-8 pb-16 pt-2 max-w-5xl">
      <Link to="/app/workouts" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors mb-5"><ArrowLeft size={14} /> Library</Link>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl overflow-hidden border border-border aspect-video bg-black">
          <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${w.youtube_id}`} title={w.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        </div>
        <div>
          <span className="text-[11px] uppercase tracking-[0.15em] text-primary font-bold">{w.category} · {w.difficulty}</span>
          <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight leading-none mt-2">{w.title}</h1>
          <div className="flex gap-5 text-sm text-muted-foreground mt-3"><span>{w.duration}</span><span>{w.calories} kcal</span></div>
          <button onClick={complete} data-testid="workout-complete" className="mt-5 bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:scale-[1.02] transition-transform">Mark Complete</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {stats.map((s) => (
          <div key={s.l} className="border border-border rounded-xl bg-card p-5">
            <s.icon size={18} className="text-primary" />
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-2">{s.l}</div>
            <div className="font-display font-800 text-2xl">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="space-y-6">
          <div className="border border-border rounded-xl bg-card p-6">
            <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-2">Instructions</h3>
            <p className="text-muted-foreground">{w.instructions}</p>
          </div>
          <div className="border border-border rounded-xl bg-card p-6">
            <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-2 flex items-center gap-2"><Shield size={18} className="text-primary" /> Safety Tips</h3>
            <p className="text-muted-foreground">{w.safety}</p>
          </div>
        </div>
        <Timer />
      </div>
    </div>
  );
}
